import axios from 'axios'
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { isZivano } from '@/utils/appBrand'
import { service } from '@/utils/net'
import ar from '../locales/ar.json'
import en from '../locales/en.json'
import tr from '../locales/tr.json'
import zivanoAr from '../locales/zivano/ar.json'
import zivanoEn from '../locales/zivano/en.json'
import zivanoTr from '../locales/zivano/tr.json'
import { getAntdLocale } from '../locales/antd'
import {
  type AppLang,
  type StaticLang,
  createI18nResources,
  deepMerge,
  detectSystemLang,
  getStaticBaseLang,
  normalizeAppLang,
  shouldEnableRtl
} from '../locales/lang'
import { parseRemoteLocales } from '../locales/parse-remote'

export { getAntdLocale }

const locales: Record<StaticLang, { translation: Record<string, any> }> = {
  en: {
    translation: en
  },
  ar: {
    translation: ar
  },
  tr: {
    translation: tr
  }
}

/** 与静态基线同 key。服务器不下发这些活动规则，合并放在远程覆盖之后。 */
const zivanoLocales: Record<StaticLang, Record<string, any>> = {
  en: zivanoEn,
  ar: zivanoAr,
  tr: zivanoTr
}

function applyZivanoRules(lang: AppLang, translation: Record<string, any>) {
  if (!isZivano()) return translation
  return deepMerge(translation, zivanoLocales[getStaticBaseLang(lang)])
}

const REMOTE_LOCALE_TIMEOUT_MS = 5000

const getLocaleCacheUrlKey = (lang: AppLang) => `locale_cache_url_${lang}`
const getLocaleCacheTextKey = (lang: AppLang) => `locale_cache_text_${lang}`

function setLang(): AppLang {
  // 只认 URL 上的 lang；有非空值则归一化（fr → en）；空则读系统语言。
  // 请求拦截器会用 localStorage.lang 覆盖 params.language，所以必须先写入归一化结果。
  const rawUrlLang = String(window.query?.lang ?? '').trim()
  const lang = rawUrlLang ? normalizeAppLang(rawUrlLang) : detectSystemLang()
  localStorage.setItem('lang', lang)
  window.lang = lang
  return lang
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(undefined), ms)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      () => {
        clearTimeout(timer)
        resolve(undefined)
      }
    )
  })
}

async function loadRemoteLocales(lang: AppLang): Promise<Record<string, any> | undefined> {
  const res = await service<IResponse<{ strResourceUrl?: string }>>({
    method: 'GET',
    url: '/doniaLiveClient/configure',
    params: { language: lang },
    silent: true
  })
  const strResourceUrl = res?.data?.data?.strResourceUrl
  if (!strResourceUrl) return undefined

  const cacheUrlKey = getLocaleCacheUrlKey(lang)
  const cacheTextKey = getLocaleCacheTextKey(lang)
  const cachedUrl = localStorage.getItem(cacheUrlKey)
  const cachedText = localStorage.getItem(cacheTextKey) || ''

  let remoteLocalesText = ''
  if (cachedUrl === strResourceUrl && cachedText) {
    remoteLocalesText = cachedText
  } else {
    const file = await axios.get<string>(strResourceUrl, {
      responseType: 'text',
      transformResponse: [(data) => data],
      timeout: 30000
    })
    remoteLocalesText = typeof file.data === 'string' ? file.data : ''
    try {
      localStorage.setItem(cacheUrlKey, strResourceUrl)
      localStorage.setItem(cacheTextKey, remoteLocalesText)
    } catch (error) {
      console.error('setupI18n locale cache failed:', error)
    }
  }

  return parseRemoteLocales(remoteLocalesText || '')
}

export async function setupI18n() {
  const lang = setLang()

  document.body.classList.toggle('mirror', shouldEnableRtl(lang))

  const remoteLocales = await withTimeout(
    loadRemoteLocales(lang).catch((error) => {
      console.error('setupI18n remote locales failed:', error)
      return undefined
    }),
    REMOTE_LOCALE_TIMEOUT_MS
  )

  const resources = createI18nResources(lang, locales, remoteLocales)
  resources[lang].translation = applyZivanoRules(lang, resources[lang].translation)

  await i18n.use(initReactI18next).init({
    lng: lang,
    fallbackLng: 'en',
    resources,
    interpolation: {
      escapeValue: false
    }
  })
}

export default i18n
